import React, { useMemo, useState, type JSX } from 'react';
import arrowRight from '@/assets/svg/static/arrow.svg';
import arrowDown from '@/assets/svg/static/arrowDown.svg';
import type { ProductReview } from '@/types/Product.types';
import { updatedImageUrl } from '@/utils/formatting';
import styles from './ProductReviewSection.module.css';
import type { ProductReviewSectionProps } from './ProductReviewSection.types';

export const ProductReviewSection = React.memo(
  ({
    reviewsData,
    ratingVal,
    ratingCountVal,
    onReviewImageClick,
  }: ProductReviewSectionProps): JSX.Element => {
    const [showAllReviews, setShowAllReviews] = useState<boolean>(false);

    // Compile flattened lists of review images and their corresponding overlays
    const { reviewImages, reviewOverlays } = useMemo(() => {
      if (!reviewsData?.reviews) {
        return {
          reviewImages: [],
          reviewOverlays: [],
        };
      }
      const images: string[] = [];
      const overlays: ProductReview[] = [];

      reviewsData.reviews.forEach((review) => {
        if (review.images && review.images.length > 0) {
          review.images.forEach((img) => {
            images.push(updatedImageUrl(img));
            overlays.push(review);
          });
        }
      });

      return {
        reviewImages: images,
        reviewOverlays: overlays,
      };
    }, [reviewsData]);

    if (!reviewsData) return <></>;

    return (
      <div className={styles.reviewsSection}>
        <div className={styles.reviewsHeader}>
          <div className={styles.reviewsHeaderLeft}>
            <span className={styles.reviewsHeaderTitle}>CUSTOMER REVIEWS</span>
            <span className={styles.reviewStars}>
              {Number(ratingVal).toFixed(1)} ★
            </span>
            <span className={styles.reviewsCount}>
              {ratingCountVal} reviews
            </span>
          </div>
          <img src={arrowRight} className={styles.headerChevron} alt="More" />
        </div>

        {reviewsData.aiReviewSummary && (
          <div className={styles.reviewsSummary}>
            {reviewsData.aiReviewSummary}
          </div>
        )}

        {/* Photos Gallery */}
        {reviewImages.length > 0 && (
          <div className={styles.customerPhotosRow}>
            {reviewImages.slice(0, 4).map((imgUrl, index) => {
              const isLast = index === 3 && reviewImages.length > 4;
              const moreCount = reviewImages.length - 3;
              return (
                <div
                  key={index}
                  className={styles.customerPhoto}
                  onClick={() =>
                    onReviewImageClick(reviewImages, index, reviewOverlays)
                  }
                >
                  <img src={imgUrl} alt={`Review photo ${index + 1}`} />
                  {isLast && (
                    <div className={styles.photoOverlay}>+{moreCount} more</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Review card list */}
        {reviewsData.reviews
          .slice(0, showAllReviews ? reviewsData.reviews.length : 2)
          .map((review, idx) => (
            <div className={styles.reviewCard} key={idx}>
              <div className={styles.reviewUserRow}>
                <span className={styles.reviewUsername}>{review.username}</span>
                <span className={styles.reviewStars}>{review.rating} ★</span>
              </div>
              <p className={styles.reviewText}>{review.text}</p>
              {review.images && review.images.length > 0 && (
                <div className={styles.reviewCardPhotos}>
                  {review.images.map((img, i, arr) => {
                    return (
                      <div
                        className={styles.reviewCardPhoto}
                        key={i}
                        onClick={() =>
                          onReviewImageClick(
                            arr.map(updatedImageUrl),
                            i,
                            arr.map(() => review)
                          )
                        }
                      >
                        <img src={updatedImageUrl(img)} alt="Review Upload" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

        {!showAllReviews && reviewsData.reviews.length > 2 && (
          <button
            className={styles.viewMoreReviewsBtn}
            onClick={() => setShowAllReviews(true)}
          >
            <span>VIEW MORE REVIEWS</span>
            <img
              src={arrowDown}
              className={styles.viewMoreChevron}
              alt="Chevron Down"
            />
          </button>
        )}
      </div>
    );
  }
);

export default ProductReviewSection;
